import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchCustomerEnrollment,
  fetchPayments,
  paySchemeInstallment,
  paySchemeInstallments, // MANUAL PAYMENT - uncomment to re-enable
} from "../customerSchemeApi";
import {
  formatDisplayDate,
  getEnrollmentPaymentDetails,
} from "../paymentPlanUtils";
import "../CustomerScheme.css";
// import qrScannerImage from "../../icici_qr_scanner.png";

const toNumber = (value) => Number(value || 0);
const getMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.message || fallback;

const getInstallmentNumber = (item, index) =>
  Number(item?.installmentNo || item?.installmentNumber || index + 1);

const getPaymentIdValue = (value) => String(value?.$oid || value || "");

const normalizePaymentStatus = (status) => String(status || "").toUpperCase();

const chooseStrongerStatus = (current, incoming) => {
  const priority = {
    REJECTED: 4,
    PENDING: 3,
    APPROVED: 2,
    SUCCESS: 2,
    PAID: 2,
    "": 0,
  };
  const currentValue = normalizePaymentStatus(current);
  const incomingValue = normalizePaymentStatus(incoming);
  return (priority[incomingValue] || 0) >= (priority[currentValue] || 0)
    ? incomingValue
    : currentValue;
};

const getInstallmentStatusMeta = (installment, installmentNo, paymentStatusById, paymentStatusByInstallmentNo) => {
  const rawInstallmentStatus = String(installment?.status || "PENDING").toUpperCase();
  if (rawInstallmentStatus === "PAID") {
    return { label: "Paid", isPaid: true };
  }

  const linkedPaymentId = getPaymentIdValue(installment?.paymentId);
  const linkedPaymentStatus = linkedPaymentId
    ? normalizePaymentStatus(paymentStatusById[linkedPaymentId])
    : normalizePaymentStatus(paymentStatusByInstallmentNo[installmentNo]);

  if (linkedPaymentStatus === "REJECTED") {
    return { label: "Rejected", isPaid: false };
  }

  if (linkedPaymentStatus === "SUCCESS" || linkedPaymentStatus === "APPROVED" || linkedPaymentStatus === "PAID") {
    return { label: "Paid", isPaid: true };
  }

  if (linkedPaymentStatus === "PENDING" || linkedPaymentId) {
    return { label: "Waiting for Approval", isPaid: false };
  }

  return { label: "Pending", isPaid: false };
};

const normalizeEnrollments = (raw) => {
  const source = raw?.data?.data || raw?.data || [];
  if (Array.isArray(source)) {
    return source;
  }
  if (Array.isArray(source?.enrollments)) {
    return source.enrollments;
  }
  if (source?.enrollment) {
    return [source.enrollment];
  }
  return source ? [source] : [];
};

const getModeUsedFromResponse = (response, fallbackMode) =>
  response?.data?.data?.modeUsed ||
  response?.data?.modeUsed ||
  response?.modeUsed ||
  fallbackMode;

const getUnitLabel = (frequency) => {
  const value = String(frequency || "").toUpperCase();
  if (value === "DAILY") return "Days";
  if (value === "WEEKLY") return "Weeks";
  return "Months";
};

const PaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authData = JSON.parse(sessionStorage.getItem("customer_scheme_auth") || "{}");
  const customerMasterId = authData?.customerMasterId;
  const customerId = authData?.customerId;
  const customerPhone = authData?.phone;
  const customerMobile = authData?.mobile;
  const identityId = customerMasterId || customerId;
  const identityCandidates = useMemo(
    () => Array.from(new Set([customerMasterId, customerId, customerPhone, customerMobile].filter(Boolean))),
    [customerMasterId, customerId, customerPhone, customerMobile]
  );
  const enrollmentCacheKey = identityId ? `customer_scheme_enrollments_cache_${identityId}` : "customer_scheme_enrollment_cache";
  const initialEnrollmentId = searchParams.get("enrollmentId") || "";
  const forcedInstallmentNumber = Number(searchParams.get("installmentNumber") || 0);
  const hasForcedInstallment = Number.isInteger(forcedInstallmentNumber) && forcedInstallmentNumber > 0;

  const [enrollments, setEnrollments] = useState([]);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(initialEnrollmentId);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false); // MANUAL PAYMENT - uncomment to re-enable
  const [razorpayProcessing, setRazorpayProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [paymentMode, setPaymentMode] = useState("COUNT");
  const [numberOfInstallments, setNumberOfInstallments] = useState(1);
  const [installmentNumbersText, setInstallmentNumbersText] = useState("");
  const [unitCount, setUnitCount] = useState(1);
  const [referenceNumber, setReferenceNumber] = useState(""); // MANUAL PAYMENT - uncomment to re-enable
  const [paymentMethod, setPaymentMethod] = useState("UPI"); // MANUAL PAYMENT - uncomment to re-enable
  const [remarks, setRemarks] = useState("");
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [lastModeUsed, setLastModeUsed] = useState("");

  const handleLogout = () => {
    localStorage.removeItem(enrollmentCacheKey);
    sessionStorage.removeItem("customer_scheme_auth");
    localStorage.removeItem("customer_scheme_auth");
    navigate("/customer/login");
  };

  const loadEnrollments = useCallback(async (preferredEnrollmentId = "") => {
    if (identityCandidates.length === 0) {
      setLoading(false);
      setError("Customer session missing. Please login again.");
      return;
    }

    try {
      setLoading(true);
      let parsedEnrollments = [];

      for (const candidate of identityCandidates) {
        try {
          const response = await fetchCustomerEnrollment(candidate);
          parsedEnrollments = normalizeEnrollments(response);
          if (parsedEnrollments.length > 0) {
            break;
          }
        } catch (innerErr) {
          void innerErr;
        }
      }

      if (parsedEnrollments.length > 0) {
        setEnrollments(parsedEnrollments);
        const fallbackId = String(parsedEnrollments[0]?._id || parsedEnrollments[0]?.id || "");
        setSelectedEnrollmentId((prev) => preferredEnrollmentId || prev || fallbackId);
        localStorage.setItem(enrollmentCacheKey, JSON.stringify(parsedEnrollments));
      } else {
        const cached = JSON.parse(localStorage.getItem(enrollmentCacheKey) || "[]");
        const cachedList = Array.isArray(cached) ? cached : [];
        setEnrollments(cachedList);
        const fallbackId = String(cachedList[0]?._id || cachedList[0]?.id || "");
        setSelectedEnrollmentId((prev) => preferredEnrollmentId || prev || fallbackId);
      }
    } catch (err) {
      const cached = JSON.parse(localStorage.getItem(enrollmentCacheKey) || "[]");
      const cachedList = Array.isArray(cached) ? cached : [];
      if (cachedList.length > 0) {
        setEnrollments(cachedList);
        const fallbackId = String(cachedList[0]?._id || cachedList[0]?.id || "");
        setSelectedEnrollmentId((prev) => preferredEnrollmentId || prev || fallbackId);
      } else {
        setError(getMessage(err, "Unable to fetch enrollment."));
      }
    } finally {
      setLoading(false);
    }
  }, [identityCandidates, enrollmentCacheKey]);

  useEffect(() => {
    loadEnrollments(initialEnrollmentId);
  }, [loadEnrollments, initialEnrollmentId]);

  const enrollment = useMemo(() => {
    if (!selectedEnrollmentId) {
      return enrollments[0] || null;
    }
    return (
      enrollments.find((item) => String(item?._id || item?.id || "") === String(selectedEnrollmentId)) ||
      enrollments[0] ||
      null
    );
  }, [enrollments, selectedEnrollmentId]);

  const paymentDetails = useMemo(() => getEnrollmentPaymentDetails(enrollment), [enrollment]);

  useEffect(() => {
    setUnitCount(1);
  }, [selectedEnrollmentId, paymentDetails.mode]);

  const installments = useMemo(() => enrollment?.installments || [], [enrollment]);

  useEffect(() => {
    const loadPaymentRecords = async () => {
      if (!enrollment) {
        setPaymentRecords([]);
        return;
      }

      try {
        const params = customerMasterId ? { customerMasterId } : customerId ? { customerId } : {};
        const response = await fetchPayments(params);
        const list = response?.data?.data || response?.data || [];
        const selectedId = String(enrollment?._id || enrollment?.id || "");
        const filtered = (Array.isArray(list) ? list : []).filter(
          (item) => String(item?.enrollmentId?.$oid || item?.enrollmentId || "") === selectedId
        );
        setPaymentRecords(filtered);
      } catch (err) {
        void err;
        setPaymentRecords([]);
      }
    };

    loadPaymentRecords();
  }, [customerMasterId, customerId, enrollment]);

  const paymentStatusById = useMemo(() => {
    const map = {};
    paymentRecords.forEach((payment) => {
      const id = getPaymentIdValue(payment?._id || payment?.id);
      if (id) {
        map[id] = payment?.status;
      }
    });
    return map;
  }, [paymentRecords]);

  const paymentStatusByInstallmentNo = useMemo(() => {
    const map = {};
    paymentRecords.forEach((payment) => {
      const status = payment?.status;
      const numbers = Array.isArray(payment?.installmentNumbers)
        ? payment.installmentNumbers
        : Array.isArray(payment?.installmentsCovered)
          ? payment.installmentsCovered
          : [];

      numbers
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0)
        .forEach((value) => {
          map[value] = chooseStrongerStatus(map[value], status);
        });

      const singleNumber = Number(payment?.installmentNumber);
      if (Number.isInteger(singleNumber) && singleNumber > 0) {
        map[singleNumber] = chooseStrongerStatus(map[singleNumber], status);
      }
    });
    return map;
  }, [paymentRecords]);

  const installmentRows = useMemo(
    () => installments.map((item, index) => {
      const installmentNo = getInstallmentNumber(item, index);
      const statusMeta = getInstallmentStatusMeta(item, installmentNo, paymentStatusById, paymentStatusByInstallmentNo);
      return { item, index, installmentNo, statusMeta };
    }),
    [installments, paymentStatusById, paymentStatusByInstallmentNo]
  );

  const pendingInstallments = useMemo(
    () => installmentRows.filter((row) => !row.statusMeta.isPaid),
    [installmentRows]
  );

  const paidInstallments = useMemo(
    () => installmentRows.filter((row) => row.statusMeta.isPaid),
    [installmentRows]
  );

  const firstOutstandingInstallment = useMemo(() => {
    const outstanding = installmentRows
      .filter((row) => !row.statusMeta.isPaid)
      .sort((a, b) => a.installmentNo - b.installmentNo);
    return outstanding[0] || null;
  }, [installmentRows]);

  const isApprovalLocked = firstOutstandingInstallment?.statusMeta?.label === "Waiting for Approval";
  const maxInstallments = Math.max(pendingInstallments.length, 1);

  const pendingInstallmentNumbers = useMemo(
    () => pendingInstallments.map((row) => Number(row.installmentNo)).filter((value) => Number.isInteger(value) && value > 0),
    [pendingInstallments]
  );

  useEffect(() => {
    if (paymentDetails.mode === "LEDGER" || !hasForcedInstallment) {
      return;
    }

    setPaymentMode("NUMBERS");
    setInstallmentNumbersText(String(forcedInstallmentNumber));
    setNumberOfInstallments(1);

    if (pendingInstallmentNumbers.length > 0 && !pendingInstallmentNumbers.includes(forcedInstallmentNumber)) {
      setError("Selected due cycle is not payable now. Please pay the next pending cycle.");
    }
  }, [paymentDetails.mode, hasForcedInstallment, forcedInstallmentNumber, pendingInstallmentNumbers]);

  const selectedInstallmentNumbers = useMemo(() => {
    if (!installmentNumbersText.trim()) {
      return [];
    }
    return installmentNumbersText
      .split(",")
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isInteger(item) && item > 0);
  }, [installmentNumbersText]);

  const normalizedSelectedInstallmentNumbers = useMemo(
    () => Array.from(new Set(selectedInstallmentNumbers)).sort((a, b) => a - b),
    [selectedInstallmentNumbers]
  );

  const normalizedPendingInstallmentNumbers = useMemo(
    () => Array.from(new Set(pendingInstallmentNumbers)).sort((a, b) => a - b),
    [pendingInstallmentNumbers]
  );

  const maxUnits = useMemo(() => {
    if (paymentDetails.mode !== "LEDGER") {
      return 0;
    }
    if (paymentDetails.selectedPaymentAmount <= 0) {
      return 0;
    }
    return Math.max(Math.floor(paymentDetails.remainingAmount / paymentDetails.selectedPaymentAmount), 0);
  }, [paymentDetails.mode, paymentDetails.remainingAmount, paymentDetails.selectedPaymentAmount]);

  const unitLabel = useMemo(
    () => getUnitLabel(paymentDetails.selectedPaymentFrequency),
    [paymentDetails.selectedPaymentFrequency]
  );

  const totalPayable = useMemo(() => {
    if (paymentDetails.mode === "LEDGER") {
      return toNumber(unitCount) * paymentDetails.selectedPaymentAmount;
    }
    if (paymentMode === "NUMBERS") {
      return paymentDetails.selectedPaymentAmount * normalizedSelectedInstallmentNumbers.length;
    }
    return paymentDetails.selectedPaymentAmount * Number(numberOfInstallments || 1);
  }, [paymentDetails, paymentMode, normalizedSelectedInstallmentNumbers, numberOfInstallments, unitCount]);

  const isUnitSelectionValid = useMemo(() => {
    if (paymentDetails.mode !== "LEDGER") {
      return true;
    }
    if (!Number.isInteger(unitCount)) {
      return false;
    }
    if (unitCount < 1) {
      return false;
    }
    if (unitCount > maxUnits) {
      return false;
    }
    if (totalPayable > paymentDetails.remainingAmount) {
      return false;
    }
    return totalPayable > 0;
  }, [paymentDetails.mode, unitCount, maxUnits, totalPayable, paymentDetails.remainingAmount]);

  const getInstallmentSelectionPayload = () => {
    if (paymentMode === "NUMBERS" && normalizedSelectedInstallmentNumbers.length === 0) {
      throw new Error("Enter due cycle numbers like 1,2");
    }

    if (isApprovalLocked) {
      throw new Error("Previous payment is waiting for approval. You cannot pay the next cycle yet.");
    }

    // Validate that all selected installments are available for payment (not already paid)
    if (paymentMode === "NUMBERS") {
      const unavailableNumbers = normalizedSelectedInstallmentNumbers.filter(
        (num) => !normalizedPendingInstallmentNumbers.includes(num)
      );
      if (unavailableNumbers.length > 0) {
        throw new Error(`These cycles cannot be paid now: ${unavailableNumbers.join(", ")}. Already paid or not available.`);
      }

      const expectedSequence = normalizedPendingInstallmentNumbers.slice(
        0,
        normalizedSelectedInstallmentNumbers.length
      );
      const isSequentialSelection =
        expectedSequence.length === normalizedSelectedInstallmentNumbers.length &&
        expectedSequence.every((value, index) => value === normalizedSelectedInstallmentNumbers[index]);

      if (!isSequentialSelection) {
        const expectedLabel = expectedSequence.length > 0 ? expectedSequence.join(", ") : "next unpaid cycles";
        throw new Error(`Please select sequential due cycles only: ${expectedLabel}.`);
      }
    }

    if (hasForcedInstallment && !normalizedPendingInstallmentNumbers.includes(forcedInstallmentNumber)) {
      throw new Error("This due cycle cannot be paid right now.");
    }

    if (paymentMode === "NUMBERS") {
      return {
        installmentNumbers: hasForcedInstallment ? [forcedInstallmentNumber] : normalizedSelectedInstallmentNumbers,
      };
    }

    return {
      numberOfInstallments: Number(numberOfInstallments),
    };
  };

  const refreshAfterSuccess = async () => {
    const currentEnrollmentId = String(enrollment?._id || enrollment?.id || "");
    if (currentEnrollmentId) {
      await loadEnrollments(currentEnrollmentId);
    }
    setUnitCount(1);
  };

  // Manual payment flow (admin approval based)
  const submitPayment = async (event) => {
    event.preventDefault();
    if (!referenceNumber.trim()) {
      setError("Reference number is required.");
      return;
    }
    if (paymentDetails.mode === "LEDGER" && !isUnitSelectionValid) {
      setError("Selected units are invalid. Please adjust units.");
      return;
    }
    try {
      setSubmitting(true);
      setError("");
      setSuccess("");
      const payload = {
        enrollmentId: enrollment?._id || enrollment?.id,
        customerId: authData?.customerId || authData?.customerMasterId,
        customerMasterId: authData?.customerMasterId || authData?.customerId,
        referenceNumber: referenceNumber.trim(),
        paymentDate: new Date().toISOString(),
        paymentMethod,
        remarks: remarks.trim() || `${paymentMethod} payment`,
      };
      if (paymentDetails.mode === "LEDGER") {
        payload.gateway = "MANUAL";
        payload.units = unitCount;
        payload.amount = totalPayable;
        payload.numberOfInstallments = Number(unitCount || 1);
      } else {
        Object.assign(payload, getInstallmentSelectionPayload());
      }
      const response = await paySchemeInstallments(payload);
      setLastModeUsed(getModeUsedFromResponse(response, paymentDetails.mode));
      setSuccess("Payment successful.");
      await refreshAfterSuccess();
    } catch (err) {
      setError(getMessage(err, "Payment submission failed."));
    } finally {
      setSubmitting(false);
    }
  };

  const payWithRazorpay = async () => {
    if (!enrollment) {
      setError("Please select an enrollment first.");
      return;
    }

    if (paymentDetails.mode === "LEDGER" && !isUnitSelectionValid) {
      setError("Selected units are invalid. Please adjust units.");
      return;
    }

    try {
      setRazorpayProcessing(true);
      setError("");
      setSuccess("");

      const payload = {
        token: authData?.accessToken,
        enrollmentId: enrollment?._id || enrollment?.id,
        remarks: remarks.trim() || "Razorpay scheme payment",
        customerName: authData?.customerName || "Customer",
        customerPhone: authData?.phone || authData?.mobile || "",
        customerEmail: authData?.email || authData?.customerEmail || "",
        gateway: "RAZORPAY",
      };

      if (paymentDetails.mode === "LEDGER") {
        payload.units = unitCount;
        payload.amount = totalPayable;
        payload.numberOfInstallments = Number(unitCount || 1);
      } else {
        Object.assign(payload, getInstallmentSelectionPayload());
      }

      const response = await paySchemeInstallment(payload);
      setLastModeUsed(getModeUsedFromResponse(response, paymentDetails.mode));
      setSuccess("Payment successful.");
      await refreshAfterSuccess();
    } catch (err) {
      setError(getMessage(err, err?.message || "Unable to complete Razorpay payment."));
    } finally {
      setRazorpayProcessing(false);
    }
  };

  const paymentFrequencyLabel =
    paymentDetails.selectedPaymentFrequency.charAt(0) + paymentDetails.selectedPaymentFrequency.slice(1).toLowerCase();

  const payButtonDisabled =
    submitting ||
    razorpayProcessing ||
    !enrollment ||
    (paymentDetails.mode === "LEDGER" && !isUnitSelectionValid);

  const shouldShowInstallmentTable =
    paymentDetails.selectedPaymentFrequency === "MONTHLY" &&
    paymentDetails.isFlexiblePayment !== true;

  const flexibleCycleSummary = useMemo(() => {
    if (shouldShowInstallmentTable) {
      return null;
    }

    const cycleAmount = toNumber(paymentDetails.selectedPaymentAmount);
    if (cycleAmount <= 0) {
      return null;
    }

    const paidCycles = Math.max(Math.floor(paymentDetails.paidAmount / cycleAmount), 0);
    const pendingCycles = Math.max(Math.ceil(paymentDetails.remainingAmount / cycleAmount), 0);
    const totalCycles = paidCycles + pendingCycles;
    const progressPercentage = totalCycles > 0
      ? Math.min(Math.round((paidCycles / totalCycles) * 100), 100)
      : 0;
    const pendingApprovals = paymentRecords.filter((item) =>
      ["PENDING", "IN_PROGRESS", "PROCESSING"].includes(normalizePaymentStatus(item?.status))
    ).length;
    const nextRecommendedAmount = pendingCycles > 0 ? cycleAmount : 0;

    return {
      cycleAmount,
      paidCycles,
      pendingCycles,
      totalCycles,
      progressPercentage,
      pendingApprovals,
      nextRecommendedAmount,
      cycleUnitLabel: getUnitLabel(paymentDetails.selectedPaymentFrequency),
    };
  }, [
    shouldShowInstallmentTable,
    paymentDetails.selectedPaymentAmount,
    paymentDetails.paidAmount,
    paymentDetails.remainingAmount,
    paymentDetails.selectedPaymentFrequency,
    paymentRecords,
  ]);

  return (
    <div className="customer-scheme-shell">
      <div className="customer-scheme-container">
        <div className="customer-header">
          <div>
            <h2>Payment Page</h2>
            <p className="customer-subtitle">
              {paymentDetails.mode === "LEDGER"
                ? "Flexible payment mode with unit-based auto calculation."
                : "Monthly payment schedule mode."}
            </p>
          </div>
          <div className="customer-row">
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/enroll")}>Enroll New Plan</button>
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/dashboard")}>Back to Dashboard</button>
            <button className="customer-btn customer-btn-secondary" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        <div className="customer-card">
          {loading ? (
            <div>Loading payment details...</div>
          ) : (
            <div className="customer-grid">
              <div className="customer-card">
                <h3>{paymentDetails.mode === "LEDGER" ? "Flexible Payment" : "Payment Summary"}</h3>
                <div className="customer-form">
                  <div className="customer-field"><label>Mode</label><input value={paymentDetails.mode} disabled /></div>
                  <div className="customer-field"><label>Total Amount</label><input value={`Rs. ${toNumber(enrollment?.totalAmount)}`} disabled /></div>
                  <div className="customer-field"><label>Paid Amount</label><input value={`Rs. ${paymentDetails.paidAmount}`} disabled /></div>
                  <div className="customer-field"><label>Remaining Amount</label><input value={`Rs. ${paymentDetails.remainingAmount}`} disabled /></div>
                  <div className="customer-field"><label>Payment Frequency</label><input value={paymentFrequencyLabel} disabled /></div>
                  <div className="customer-field"><label>Suggested Amount Per Unit</label><input value={`Rs. ${paymentDetails.selectedPaymentAmount}`} disabled /></div>
                  <div className="customer-field"><label>Next Due Date</label><input value={formatDisplayDate(paymentDetails.nextDueDate)} disabled /></div>
                  {lastModeUsed && <div className="customer-success">API modeUsed: {lastModeUsed}</div>}
                  {/* QR block kept disabled because image asset is not present in this workspace */}
                </div>
              </div>

              <div className="customer-card">
                <h3>Pay With Razorpay</h3>
                {/* MANUAL PAYMENT - to re-enable: change onSubmit to submitPayment */}
                <form className="customer-form" onSubmit={submitPayment}>
                  <div className="customer-field">
                    <label>Scheme Enrollment</label>
                    <select
                      value={String(selectedEnrollmentId || enrollment?._id || enrollment?.id || "")}
                      onChange={(e) => setSelectedEnrollmentId(e.target.value)}
                    >
                      {enrollments.length === 0 ? (
                        <option value="">No enrollment found</option>
                      ) : (
                        enrollments.map((item, idx) => {
                          const value = String(item?._id || item?.id || `enr-${idx}`);
                          const label = `${item?.schemeName || "Scheme"} - ${item?.productCode || item?.comboCode || "-"}`;
                          return (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          );
                        })
                      )}
                    </select>
                  </div>

                  {paymentDetails.mode === "LEDGER" ? (
                    <>
                      <div className="customer-field">
                        <label>{`Select ${unitLabel}`}</label>
                        <div className="customer-row" style={{ alignItems: "center", gap: 12 }}>
                          <button
                            type="button"
                            className="customer-btn customer-btn-secondary"
                            onClick={() => setUnitCount((prev) => Math.max(1, prev - 1))}
                            disabled={unitCount <= 1 || razorpayProcessing}
                          >
                            -
                          </button>
                          <input
                            value={unitCount}
                            onChange={(e) => {
                              const nextValue = Number(e.target.value);
                              if (Number.isInteger(nextValue)) {
                                setUnitCount(nextValue);
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="customer-btn customer-btn-secondary"
                            onClick={() => setUnitCount((prev) => Math.min(maxUnits || 1, prev + 1))}
                            disabled={unitCount >= maxUnits || razorpayProcessing || maxUnits < 1}
                          >
                            +
                          </button>
                        </div>
                        <div className="customer-helper-text">{`Min: 1 | Max: ${maxUnits}`}</div>
                      </div>

                      <div className="customer-success">
                        {`You are paying for ${unitCount} ${unitLabel.toLowerCase()}`}
                      </div>
                      <div className="customer-success">
                        {`Total Payable: Rs. ${totalPayable}`}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="customer-row">
                        <button
                          type="button"
                          className={`customer-btn ${paymentMode === "COUNT" ? "customer-btn-primary" : "customer-btn-secondary"}`}
                          onClick={() => setPaymentMode("COUNT")}
                          disabled={hasForcedInstallment}
                        >
                          By Number Of Due Cycles
                        </button>
                        <button
                          type="button"
                          className={`customer-btn ${paymentMode === "NUMBERS" ? "customer-btn-primary" : "customer-btn-secondary"}`}
                          onClick={() => setPaymentMode("NUMBERS")}
                          disabled={hasForcedInstallment}
                        >
                          By Due Cycle Numbers
                        </button>
                      </div>

                      {paymentMode === "COUNT" ? (
                        <div className="customer-field">
                          <label>Number of Due Cycles</label>
                          <select value={numberOfInstallments} onChange={(e) => setNumberOfInstallments(e.target.value)}>
                            {Array.from({ length: maxInstallments }, (_, index) => index + 1).map((item) => (
                              <option key={item} value={item}>{item}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div className="customer-field">
                          <label>Due Cycle Numbers (comma-separated)</label>
                          <input
                            value={installmentNumbersText}
                            onChange={(e) => setInstallmentNumbersText(e.target.value)}
                            placeholder="Example: 1,2"
                            disabled={hasForcedInstallment}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Manual payment fields */}
                  <div className="customer-field">
                    <label>Reference Number</label>
                    <input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="Enter transaction reference" />
                  </div>
                  <div className="customer-field">
                    <label>Payment Method</label>
                    <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                      <option value="UPI">UPI</option>
                      <option value="CASH">CASH</option>
                      <option value="CARD">CARD</option>
                    </select>
                  </div>
                  <div className="customer-field">
                    <label>Remarks</label>
                    <input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Payment note (optional)" />
                  </div>

                  {error && <div className="customer-error">{error}</div>}
                  {success && <div className="customer-success">{success}</div>}

                  <div className="customer-row">
                    {/* Manual payment submit + Razorpay */}
                    <button className="customer-btn customer-btn-primary" type="submit" disabled={payButtonDisabled}>
                      {submitting ? "Submitting..." : "Submit Payment"}
                    </button>
                    <button className="customer-btn customer-btn-secondary" type="button" onClick={payWithRazorpay} disabled={payButtonDisabled}>
                      {razorpayProcessing ? "Opening Razorpay..." : "Pay With Razorpay"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {!loading && shouldShowInstallmentTable && (
            <div className="customer-grid" style={{ marginTop: 14 }}>
              <div className="customer-card customer-table-wrap">
                <h3>Pending Due Cycles</h3>
                <table className="customer-table">
                  <thead><tr><th>Cycle No</th><th>Amount</th><th>Status</th></tr></thead>
                  <tbody>
                    {pendingInstallments.length === 0 ? (
                      <tr><td colSpan="3">No pending due cycles.</td></tr>
                    ) : (
                      pendingInstallments.map(({ item, index, installmentNo, statusMeta }) => (
                        <tr key={item?._id || index}>
                          <td>{installmentNo}</td>
                          <td>Rs. {toNumber(item?.amount || paymentDetails.selectedPaymentAmount)}</td>
                          <td>
                            <span
                              className={`customer-status ${
                                statusMeta.label === "Rejected"
                                  ? "customer-status-rejected"
                                  : "customer-status-pending"
                              }`}
                            >
                              {statusMeta.label}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="customer-card customer-table-wrap">
                <h3>Paid Due Cycles</h3>
                <table className="customer-table">
                  <thead><tr><th>Cycle No</th><th>Amount</th><th>Status</th></tr></thead>
                  <tbody>
                    {paidInstallments.length === 0 ? (
                      <tr><td colSpan="3">No paid due cycles yet.</td></tr>
                    ) : (
                      paidInstallments.map(({ item, index, installmentNo }) => (
                        <tr key={item?._id || index}>
                          <td>{installmentNo}</td>
                          <td>Rs. {toNumber(item?.amount || paymentDetails.selectedPaymentAmount)}</td>
                          <td><span className="customer-status customer-status-paid">Paid</span></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && !shouldShowInstallmentTable && (
            <div className="customer-grid" style={{ marginTop: 14 }}>
              {flexibleCycleSummary && (
                <div className="customer-card">
                  <h3>Cycle Progress</h3>
                  <div className="customer-dashboard-grid" style={{ marginBottom: 10 }}>
                    <div className="customer-metric">
                      <div className="customer-metric-label">Paid Cycles</div>
                      <div className="customer-metric-value">{flexibleCycleSummary.paidCycles}</div>
                    </div>
                    <div className="customer-metric">
                      <div className="customer-metric-label">Pending Cycles</div>
                      <div className="customer-metric-value">{flexibleCycleSummary.pendingCycles}</div>
                    </div>
                    <div className="customer-metric">
                      <div className="customer-metric-label">Pending Approvals</div>
                      <div className="customer-metric-value">{flexibleCycleSummary.pendingApprovals}</div>
                    </div>
                    <div className="customer-metric">
                      <div className="customer-metric-label">Per Cycle Amount</div>
                      <div className="customer-metric-value">Rs. {flexibleCycleSummary.cycleAmount}</div>
                    </div>
                  </div>
                  <div className="customer-helper-text" style={{ marginBottom: 6 }}>
                    {`Cycle Unit: ${flexibleCycleSummary.cycleUnitLabel} | Total Cycles: ${flexibleCycleSummary.totalCycles}`}
                  </div>
                  <div className="customer-progress-track">
                    <div
                      className="customer-progress-fill"
                      style={{ width: `${flexibleCycleSummary.progressPercentage}%` }}
                    />
                  </div>
                  <div className="customer-helper-text" style={{ marginTop: 6 }}>
                    {`Progress: ${flexibleCycleSummary.progressPercentage}%`}
                  </div>
                  <div className="customer-helper-text" style={{ marginTop: 6, fontWeight: 600 }}>
                    {flexibleCycleSummary.pendingCycles > 0
                      ? `Next recommended payment: Rs. ${flexibleCycleSummary.nextRecommendedAmount} for 1 ${flexibleCycleSummary.cycleUnitLabel.slice(0, -1) || "cycle"}`
                      : "All due cycles are completed."}
                  </div>
                </div>
              )}

              <div className="customer-card customer-table-wrap">
                <h3>Payment History</h3>
                <table className="customer-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentRecords.length === 0 ? (
                      <tr><td colSpan="3">No payment history available.</td></tr>
                    ) : (
                      paymentRecords.map((item, index) => (
                        <tr key={item?._id || item?.id || index}>
                          <td>
                            {item?.paymentDate
                              ? new Date(item.paymentDate).toLocaleDateString()
                              : item?.date
                                ? new Date(item.date).toLocaleDateString()
                                : "-"}
                          </td>
                          <td>Rs. {toNumber(item?.paidAmount || item?.amount)}</td>
                          <td>{normalizePaymentStatus(item?.status || "PENDING")}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
